import { Link, useLocation } from "react-router-dom";
import { useState } from "react";
import { ShoppingBag, Menu, X, ChevronDown } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { LangCurrencyControls } from "@/components/LangCurrencyControls";
import { BRAND } from "@/lib/api";

const NAV = [
  { to: "/shop", label: "Shop" },
  { to: "/build-your-own-box", label: "Build Your Box" },
  { to: "/subscriptions", label: "Subscribe" },
];

const PROGRAMS = [
  { to: "/care-boxes", label: "Care Boxes" },
  { to: "/hope-cards", label: "Keepsake Hope Cards" },
  { to: "/grace-in-action", label: "Hope in Action" },
  { to: "/corporate-care", label: "Corporate Care" },
  { to: "/church-ministry", label: "Church Ministry" },
  { to: "/hospital-partnerships", label: "Hospital Partnerships" },
  { to: "/subscriptions", label: "Monthly Encouragement Club" },
];

const NAV_END = [
  { to: "/digital-downloads", label: "Digital" },
  { to: "/about", label: "About" },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const { count } = useCart();
  const [open, setOpen] = useState(false);
  const [progOpen, setProgOpen] = useState(false);
  const loc = useLocation();

  const linkCls = (to) => `gh-link-underline text-[13px] tracking-[0.06em] transition-colors ${loc.pathname === to ? "text-[#8E9A86]" : "text-[#3A3632] hover:text-[#8E9A86]"}`;

  return (
    <header className="sticky top-0 z-50 bg-[#FAF7F2]/90 backdrop-blur-md border-b border-[#D9CBA0]/40">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between h-32">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-4 group">
          <div className="h-28 w-28 gh-octagon bg-[#1a1714] flex items-center justify-center overflow-hidden ring-1 ring-[#D9CBA0]/50">
            <img src={BRAND.logo} alt="Hope & Favor" className="h-full w-full object-contain" />
          </div>
          <div className="leading-tight">
            <div className="font-heading text-5xl text-[#3A3632] tracking-tight">Hope &amp; Favor</div>
            <div className="gh-overline text-[10px]">Thoughtfully Curated · Beautifully Given</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-6">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} data-testid={`nav-${n.to.slice(1)}`} className={linkCls(n.to)}>{n.label}</Link>
          ))}
          <div className="relative" onMouseEnter={() => setProgOpen(true)} onMouseLeave={() => setProgOpen(false)}>
            <button data-testid="nav-programs" className="gh-link-underline text-[13px] tracking-[0.06em] text-[#3A3632] hover:text-[#8E9A86] inline-flex items-center gap-1">
              Programs <ChevronDown className="h-3.5 w-3.5" />
            </button>
            {progOpen && (
              <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 z-50" data-testid="programs-menu">
                <div className="bg-[#FAF7F2] border border-[#D9CBA0]/50 shadow-[0_18px_50px_rgba(26,26,26,0.12)] rounded-xl p-2 w-64">
                  {PROGRAMS.map((p) => (
                    <Link key={p.to} to={p.to} data-testid={`nav-${p.to.slice(1)}`}
                      className={`block px-4 py-2.5 rounded-lg text-[13px] transition-colors ${loc.pathname === p.to ? "text-[#8E9A86] bg-[#8E9A86]/8" : "text-[#3A3632] hover:bg-[#F2EFEB] hover:text-[#8E9A86]"}`}>
                      {p.label}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
          {NAV_END.map((n) => (
            <Link key={n.to} to={n.to} data-testid={`nav-${n.to.slice(1)}`} className={linkCls(n.to)}>{n.label}</Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="hidden md:block"><LangCurrencyControls /></div>
          <Link to="/cart" data-testid="cart-link" className="relative p-2 rounded-full hover:bg-[#F2EFEB] transition-colors">
            <ShoppingBag className="h-5 w-5 text-[#3A3632]" />
            {count > 0 && (
              <span data-testid="cart-count" className="absolute -top-0.5 -right-0.5 h-5 w-5 rounded-full bg-[#C4A4A4] text-white text-[11px] flex items-center justify-center font-semibold">{count}</span>
            )}
          </Link>
          <button data-testid="mobile-menu-btn" className="lg:hidden p-2" onClick={() => setOpen(!open)}>
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <nav className="lg:hidden border-t border-[#E5DFD5] bg-[#FAF7F2] px-5 py-4 flex flex-col gap-1 max-h-[70vh] overflow-y-auto">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} onClick={() => setOpen(false)} data-testid={`mobile-nav-${n.to.slice(1)}`}
              className="py-2.5 text-[#3A3632] font-medium border-b border-[#E5DFD5]/60">{n.label}</Link>
          ))}
          <div className="py-2 gh-overline text-[10px]">Programs</div>
          {PROGRAMS.map((p) => (
            <Link key={p.to} to={p.to} onClick={() => setOpen(false)} data-testid={`mobile-nav-${p.to.slice(1)}`}
              className="py-2 pl-3 text-[#6B655F] text-sm border-b border-[#E5DFD5]/50">{p.label}</Link>
          ))}
          {NAV_END.map((n) => (
            <Link key={n.to} to={n.to} onClick={() => setOpen(false)} data-testid={`mobile-nav-${n.to.slice(1)}`}
              className="py-2.5 text-[#3A3632] font-medium border-b border-[#E5DFD5]/60 last:border-0">{n.label}</Link>
          ))}
          <div className="pt-3"><LangCurrencyControls /></div>
        </nav>
      )}
    </header>
  );
};
